import { Box, Text, useColorModeValue } from '@ecoinvent/ui';

import { formatNumericalValue } from 'utilities/numbers';

import { COLORS, SUPPLY_CARD_TEXT_INDENT } from './constants';

// `empty` is a real answer (the selection has no/`null` score for this node); `notAsked` is the absence of a
// selection and `error` is a request that got no answer. `ready` carries the raw per-reference-unit number and
// its already-formatted unit (resolved once at the root, so every card shares one string).
export type ImpactScoreState =
  | { status: 'notAsked' }
  | { status: 'pending' }
  | { status: 'empty' }
  | { status: 'error' }
  | { status: 'ready'; score: number; unit: string };

// `isLive` only for the root and the selected card: a live region on every card queues one identical,
// unattributed phrase per card whenever a filter changes, which is worse than announcing nothing.
type Props = { state: ImpactScoreState; isLive?: boolean };

// Every state has to occupy exactly the text row or a value replacing the shimmer nudges the centred card, so
// the row is derived from the same two tokens the text sets, and follows them if the theme changes either.
const SCORE_FONT_SIZE = 'sm';
const SCORE_LINE_HEIGHT = 'base';
const SCORE_ROW_HEIGHT = `calc(var(--chakra-fontSizes-${SCORE_FONT_SIZE}) * var(--chakra-lineHeights-${SCORE_LINE_HEIGHT}))`;
// Both have to stay token *names*: they are interpolated into the CSS var names, so a raw `14px` or `1.4`
// (here or on either <Text> below) leaves an undefined var, voiding the calc and silently dropping the row.

const ImpactScore = ({ state, isLive }: Props) => {
  const scoreColor = useColorModeValue(COLORS.textLight, COLORS.textDark);
  const dimColor = useColorModeValue(COLORS.grayDark, COLORS.grayDim);

  const renderState = () => {
    // `notAsked` shimmers too: asserting "no score" before a selection exists reads as a result.
    if (state.status === 'notAsked' || state.status === 'pending') {
      return (
        <Box pl={`${SUPPLY_CARD_TEXT_INDENT}px`} h={SCORE_ROW_HEIGHT} display="flex" alignItems="center">
          <Box bg={dimColor} opacity={0.25} borderRadius="sm" h="12px" w="55%" />
        </Box>
      );
    }

    // Most datasets carry a handful of the release's methods, so a selection with no row here is routine.
    if (state.status === 'empty' || state.status === 'error') {
      // fontSize/lineHeight: token names only (SCORE_ROW_HEIGHT).
      return (
        <Text
          pl={`${SUPPLY_CARD_TEXT_INDENT}px`}
          fontSize={SCORE_FONT_SIZE}
          lineHeight={SCORE_LINE_HEIGHT}
          color={dimColor}
          noOfLines={1}
        >
          {state.status === 'empty' ? 'No score for this selection' : 'Score unavailable'}
        </Text>
      );
    }

    // fontSize/lineHeight: token names only (SCORE_ROW_HEIGHT).
    return (
      <Text
        pl={`${SUPPLY_CARD_TEXT_INDENT}px`}
        fontSize={SCORE_FONT_SIZE}
        lineHeight={SCORE_LINE_HEIGHT}
        fontWeight="bold"
        color={scoreColor}
        noOfLines={1}
      >
        {formatNumericalValue(state.score)}{' '}
        <Text as="span" fontWeight="normal" color={dimColor}>
          {state.unit}
        </Text>
      </Text>
    );
  };

  // One wrapper kept across every state: screen readers miss a live region inserted already populated.
  return (
    <Box minH={SCORE_ROW_HEIGHT} role={isLive ? 'status' : undefined} aria-live={isLive ? 'polite' : undefined}>
      {renderState()}
    </Box>
  );
};

export default ImpactScore;
